import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/motion/Reveal";
import { Eyebrow } from "@/components/primitives";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { brand } from "@/content/site";
import { track } from "@/lib/analytics";
import CalendarPicker from "@/components/sections/CalendarPicker";
import {
  Sun,
  Battery,
  Thermometer,
  Zap,
  Wind,
  MoreHorizontal,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Phone,
  ShieldCheck,
  Check,
  CalendarCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCTS = [
  { key: "Solar", Icon: Sun },
  { key: "Battery", Icon: Battery },
  { key: "Heat Pump", Icon: Thermometer },
  { key: "EV Chargers", Icon: Zap },
  { key: "Air Con Systems", Icon: Wind },
  { key: "Others", Icon: MoreHorizontal },
];

const TOTAL_STEPS = 4;

const PILOT_META = {
  "energy-growth-pilot": {
    label: "Energy Growth Pilot",
    tagline:
      "For solar, battery & home-energy retailers with their own sales team.",
    productPrompt: "Which products do you want to grow sales in?",
  },
  "installer-growth-pilot": {
    label: "Installer Growth Pilot",
    tagline:
      "For solar & battery installers building a direct pipeline under their own brand.",
    productPrompt: "Which products do you install?",
  },
};

function Stepper({ current }) {
  return (
    <div
      data-testid="discovery-stepper"
      className="flex items-center gap-2"
      aria-label={`Step ${current} of ${TOTAL_STEPS}`}
    >
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
        const idx = i + 1;
        const done = idx < current;
        const active = idx === current;
        return (
          <div key={idx} className="flex items-center gap-2">
            <div
              className={
                "h-8 w-8 rounded-full inline-flex items-center justify-center text-xs font-bold transition-colors " +
                (done
                  ? "bg-forest text-lime"
                  : active
                  ? "bg-lime text-forest ring-4 ring-lime/20"
                  : "bg-soft text-mid-neutral border border-border-neutral")
              }
            >
              {done ? <Check className="h-4 w-4" strokeWidth={3} /> : idx}
            </div>
            {idx < TOTAL_STEPS && (
              <span
                className={
                  "h-0.5 w-6 sm:w-10 " +
                  (idx < current ? "bg-forest" : "bg-border-neutral")
                }
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Discovery({ pilot = "energy-growth-pilot" }) {
  const navigate = useNavigate();
  const meta = PILOT_META[pilot] || PILOT_META["energy-growth-pilot"];

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [products, setProducts] = useState([]);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [notes, setNotes] = useState("");
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");

  // otp state
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [verificationToken, setVerificationToken] = useState("");
  const [normalisedPhone, setNormalisedPhone] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  // calendar state
  const [selectedSlotIso, setSelectedSlotIso] = useState("");
  const [selectedSlotLabel, setSelectedSlotLabel] = useState("");

  const [pageMeta, setPageMeta] = useState({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setPageMeta({
      landingPage: window.location.href,
      referrer: document.referrer || "",
      utmSource: params.get("utm_source") || "",
      utmMedium: params.get("utm_medium") || "",
      utmCampaign: params.get("utm_campaign") || "",
      utmTerm: params.get("utm_term") || "",
      utmContent: params.get("utm_content") || "",
      refToken: params.get("ref") || sessionStorage.getItem("gc_ref") || "",
    });
    track("discovery_view", { pilot });
  }, [pilot]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const toggleProduct = (p) => {
    setProducts((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );
  };

  const canNextStep1 =
    firstName.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canNextStep2 = products.length > 0;

  const goNext = () => {
    setError("");
    if (step === 1 && !canNextStep1) {
      setError("Please add your first name and a valid email.");
      return;
    }
    if (step === 2 && !canNextStep2) {
      setError("Please pick at least one product.");
      return;
    }
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };

  const goBack = () => {
    setError("");
    setStep((s) => Math.max(1, s - 1));
  };

  const sendOtp = async () => {
    setError("");
    if (!phone.trim()) {
      setError("Please enter your mobile number.");
      return;
    }
    if (!consent) {
      setError("Please acknowledge the privacy statement to receive the code.");
      return;
    }
    setOtpSending(true);
    try {
      const res = await axios.post(`${API}/otp/send`, { phone });
      setNormalisedPhone(res.data.phone);
      setOtpSent(true);
      setResendCooldown(30);
      track("discovery_otp_sent", { pilot });
      if (res.data.devCode) {
        toast.info("Dev mode: code shown below", {
          description: `Your code is ${res.data.devCode}`,
        });
      } else {
        toast.success("Code sent", {
          description: `We've SMS'd a 6-digit code to ${res.data.phone}.`,
        });
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "We couldn't send the code. Please check the number and try again.";
      setError(detail);
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtp = async () => {
    setError("");
    if (code.trim().length < 4) {
      setError("Please enter the 6-digit code we sent.");
      return;
    }
    setOtpVerifying(true);
    try {
      const vres = await axios.post(`${API}/otp/verify`, {
        phone: normalisedPhone || phone,
        code: code.trim(),
      });
      setVerificationToken(vres.data.verificationToken);
      track("discovery_otp_verified", { pilot });
      // Advance to calendar step
      setStep(4);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "That code doesn't match. Please try again.";
      setError(detail);
    } finally {
      setOtpVerifying(false);
    }
  };

  const confirmBooking = async () => {
    setError("");
    if (!selectedSlotIso) {
      setError("Please pick a time slot to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const bres = await axios.post(`${API}/discovery-bookings`, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        businessName: businessName.trim(),
        pilotInterest: pilot,
        products,
        phone: normalisedPhone || phone,
        verificationToken,
        slot: selectedSlotIso,
        notes: notes.trim(),
        company_website: honeypot,
        ...pageMeta,
      });
      if (bres.data?.id) {
        track("discovery_booking_submitted", {
          pilot,
          slotIso: selectedSlotIso,
          products: products.join(","),
        });
        navigate(
          `/discovery/thank-you?slot=${encodeURIComponent(
            bres.data.slotLabel || selectedSlotLabel
          )}&pilot=${pilot}`
        );
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setError(detail);
      track("discovery_error", { status: err?.response?.status || "network" });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "h-14 bg-white border-border-neutral rounded-xl focus-visible:ring-mint text-base";

  return (
    <>
      <SEO
        title={`Book Your Discovery Call | ${meta.label} | Green Circle Group`}
        description={`Book a complimentary 30-minute Discovery Call for the ${meta.label}.`}
        path={`/discovery/${pilot}`}
        noindex
      />

      <section
        data-testid={`discovery-page-${pilot}`}
        data-pilot={pilot}
        className="relative bg-white pt-28 md:pt-36 pb-24 min-h-screen"
      >
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <div className="text-center">
              <Eyebrow className="justify-center">
                {meta.label} — Discovery Call
              </Eyebrow>
              <h1 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.08] text-forest text-balance">
                Let&apos;s find a good time to chat.
              </h1>
              <p className="mt-4 text-mid-neutral leading-relaxed max-w-xl mx-auto">
                Four quick steps to lock in your complimentary 30-minute
                Discovery Call. {meta.tagline}
              </p>
            </div>
          </Reveal>

          <div className="mt-10 flex justify-center">
            <Stepper current={step} />
          </div>

          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="hidden"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />

          <div className="mt-10 rounded-[2rem] border border-border-neutral bg-white p-6 md:p-10 shadow-[0_20px_60px_rgba(3,34,26,0.05)]">
            {/* STEP 1 — Your details */}
            {step === 1 && (
              <div data-testid="discovery-step-1" className="space-y-5">
                <div>
                  <h2 className="text-xl md:text-2xl font-extrabold text-forest">
                    Who are we talking to?
                  </h2>
                  <p className="mt-2 text-mid-neutral">
                    Just your name and email to get started.
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First name *</Label>
                    <Input
                      id="firstName"
                      data-testid="discovery-firstName"
                      className={inputCls}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      autoComplete="given-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last name</Label>
                    <Input
                      id="lastName"
                      data-testid="discovery-lastName"
                      className={inputCls}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      autoComplete="family-name"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="discovery-email"
                    className={inputCls}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </div>
                <div>
                  <Label htmlFor="businessName">Business name (optional)</Label>
                  <Input
                    id="businessName"
                    data-testid="discovery-businessName"
                    className={inputCls}
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    autoComplete="organization"
                  />
                </div>
              </div>
            )}

            {/* STEP 2 — Products */}
            {step === 2 && (
              <div data-testid="discovery-step-2" className="space-y-5">
                <div>
                  <h2 className="text-xl md:text-2xl font-extrabold text-forest">
                    {meta.productPrompt}
                  </h2>
                  <p className="mt-2 text-mid-neutral">
                    Pick as many as you like — we&apos;ll focus the pilot around one.
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {PRODUCTS.map(({ key, Icon }) => {
                    const active = products.includes(key);
                    return (
                      <button
                        key={key}
                        type="button"
                        data-testid={`discovery-product-${key
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                        onClick={() => toggleProduct(key)}
                        className={
                          "text-left rounded-2xl border-2 p-5 transition-all " +
                          (active
                            ? "border-forest bg-forest text-white"
                            : "border-border-neutral hover:border-mint bg-white text-forest")
                        }
                      >
                        <div
                          className={
                            "inline-flex h-9 w-9 items-center justify-center rounded-full mb-3 " +
                            (active
                              ? "bg-lime text-forest"
                              : "bg-soft text-mint")
                          }
                        >
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="text-base font-extrabold">{key}</div>
                      </button>
                    );
                  })}
                </div>
                <div>
                  <Label htmlFor="notes">Anything specific we should know? (optional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="discovery-notes"
                    rows={3}
                    className="bg-white border-border-neutral rounded-xl focus-visible:ring-mint text-base"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. State, service area, current lead sources…"
                  />
                </div>
              </div>
            )}

            {/* STEP 3 — Mobile OTP */}
            {step === 3 && (
              <div data-testid="discovery-step-3" className="space-y-5">
                <div className="text-center">
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-forest text-lime">
                    <Phone className="h-6 w-6" />
                  </div>
                  <h2 className="mt-5 text-xl md:text-2xl font-extrabold text-forest">
                    One quick verification, {firstName || "friend"} 👋
                  </h2>
                  <p className="mt-2 text-mid-neutral">
                    Verify your mobile so we can send you the calendar invite.
                    We&apos;ll only text you when it matters.
                  </p>
                </div>

                {!otpSent ? (
                  <>
                    <div>
                      <Label htmlFor="phone">Australian mobile *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="discovery-phone"
                        className={inputCls}
                        placeholder="0412 345 678"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        autoComplete="tel"
                        inputMode="tel"
                      />
                      <p className="mt-1.5 text-xs text-mid-neutral">
                        We&apos;ll SMS you a 6-digit verification code.
                      </p>
                    </div>

                    <div className="flex items-start gap-3 rounded-xl bg-soft p-4">
                      <Checkbox
                        id="consent"
                        data-testid="discovery-consent"
                        checked={consent}
                        onCheckedChange={setConsent}
                        className="mt-0.5 border-mid-neutral data-[state=checked]:bg-forest data-[state=checked]:border-forest"
                      />
                      <Label
                        htmlFor="consent"
                        className="text-sm text-mid-neutral leading-relaxed cursor-pointer"
                      >
                        I agree to be contacted about my Discovery Call and
                        acknowledge the{" "}
                        <a
                          href="/privacy"
                          className="font-semibold text-forest underline"
                        >
                          Privacy Policy
                        </a>
                        .
                      </Label>
                    </div>

                    <button
                      type="button"
                      onClick={sendOtp}
                      disabled={otpSending}
                      data-testid="discovery-send-otp"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-lime px-8 py-4 text-base font-bold text-forest transition-all duration-300 hover:bg-bright-green hover:-translate-y-0.5 shadow-[0_8px_24px_rgba(141,254,63,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 disabled:opacity-60"
                    >
                      {otpSending ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" /> Sending code…
                        </>
                      ) : (
                        <>
                          Send verification code <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="rounded-xl bg-soft border border-border-neutral p-4 text-sm text-forest">
                      <ShieldCheck className="inline h-4 w-4 mr-1.5 text-mint" />
                      Code sent to <strong>{normalisedPhone}</strong>.
                    </div>
                    <div>
                      <Label htmlFor="code">6-digit code *</Label>
                      <Input
                        id="code"
                        type="text"
                        data-testid="discovery-otp-code"
                        className={
                          inputCls +
                          " text-center tracking-[0.6em] font-bold text-2xl"
                        }
                        placeholder="••••••"
                        maxLength={6}
                        value={code}
                        onChange={(e) =>
                          setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                        }
                        inputMode="numeric"
                        autoComplete="one-time-code"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={verifyOtp}
                      disabled={otpVerifying}
                      data-testid="discovery-verify-otp"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-lime px-8 py-4 text-base font-bold text-forest transition-all duration-300 hover:bg-bright-green hover:-translate-y-0.5 shadow-[0_8px_24px_rgba(141,254,63,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 disabled:opacity-60"
                    >
                      {otpVerifying ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" /> Verifying…
                        </>
                      ) : (
                        <>
                          Verify &amp; pick a time <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </button>

                    <div className="text-center text-sm">
                      <button
                        type="button"
                        onClick={sendOtp}
                        disabled={resendCooldown > 0 || otpSending}
                        data-testid="discovery-resend-otp"
                        className="text-forest font-semibold underline disabled:no-underline disabled:text-mid-neutral"
                      >
                        {resendCooldown > 0
                          ? `Resend code in ${resendCooldown}s`
                          : "Resend code"}
                      </button>
                      {" · "}
                      <button
                        type="button"
                        onClick={() => {
                          setOtpSent(false);
                          setCode("");
                        }}
                        className="text-forest font-semibold underline"
                      >
                        Change number
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* STEP 4 — Calendar */}
            {step === 4 && (
              <div data-testid="discovery-step-4" className="space-y-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-extrabold text-forest">
                    Choose a time for your Discovery Call
                  </h2>
                  <p className="mt-2 text-mid-neutral">
                    30-minute chat with the {brand.name} team.
                    The earliest slot is 24 hours from now — pick whatever works.
                  </p>
                </div>

                <CalendarPicker
                  selectedSlot={selectedSlotIso}
                  onSelect={(iso, info) => {
                    setSelectedSlotIso(iso);
                    setSelectedSlotLabel(info.label);
                    track("discovery_slot_selected", { pilot, slotIso: iso });
                  }}
                />

                {selectedSlotIso && (
                  <div
                    data-testid="discovery-selected-slot"
                    className="rounded-2xl bg-forest text-white p-5 flex items-start gap-3"
                  >
                    <CalendarCheck className="h-5 w-5 text-lime shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.15em] text-lime">
                        Selected time
                      </p>
                      <p className="mt-1 text-white font-extrabold">
                        {selectedSlotLabel}
                      </p>
                      <p className="mt-1 text-xs text-white/70">
                        Sydney time (AEST/AEDT). We&apos;ll email you a calendar invite.
                      </p>
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={confirmBooking}
                  disabled={!selectedSlotIso || submitting}
                  data-testid="discovery-confirm-booking"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-lime px-8 py-4 text-base font-bold text-forest transition-all duration-300 hover:bg-bright-green hover:-translate-y-0.5 shadow-[0_8px_24px_rgba(141,254,63,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" /> Booking…
                    </>
                  ) : (
                    <>
                      Confirm my Discovery Call <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            )}

            {error && (
              <div
                role="alert"
                data-testid="discovery-error"
                className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
              >
                {error} You can also email{" "}
                <a
                  href={`mailto:${brand.email}`}
                  className="font-semibold underline"
                >
                  {brand.email}
                </a>
                .
              </div>
            )}

            {/* Nav buttons */}
            {step < 3 && (
              <div className="mt-8 flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={goBack}
                  disabled={step === 1}
                  data-testid="discovery-back"
                  className="inline-flex items-center gap-2 text-sm font-bold text-mid-neutral hover:text-forest disabled:opacity-40"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  data-testid="discovery-next"
                  className="inline-flex items-center gap-2 rounded-full bg-forest text-white px-7 py-3.5 text-sm font-bold hover:bg-dark-neutral transition-colors"
                >
                  Continue <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}
            {(step === 3 || step === 4) && (
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={goBack}
                  data-testid={`discovery-back-step${step}`}
                  className="inline-flex items-center gap-2 text-sm font-bold text-mid-neutral hover:text-forest"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
              </div>
            )}
          </div>

          <p className="mt-8 text-center text-xs text-mid-neutral">
            {brand.name} — powered by real conversations, not automated gimmicks.
          </p>
        </div>
      </section>
    </>
  );
}
